import { Image } from 'expo-image';
import { StyleSheet } from 'react-native';

import {
  type ImageAssetWithAltText,
  RadioGroup,
  Skeleton,
  Text,
  YStack,
  tokens,
  usePressableState,
  useRadioItem,
} from '@fhs/ui';

import type { PickerAspectOptionSelectorProps } from './types';

export function HorizontalPickerAspectOptionSelector(props: PickerAspectOptionSelectorProps) {
  return (
    <RadioGroup style={styles.radioGroup} value={props.value} onChange={props.onChange}>
      {props.options.map(option => (
        <RadioGroup.Item
          key={option.value}
          value={option.value}
          label={[option.title, option.subtitle].filter(Boolean).join(', ')}
          disabled={option.disabled}
        >
          <PickerAspectCard
            title={option.title}
            subtitle={option.subtitle}
            image={option.image}
            bufferImage={option.bufferImage}
            useSkeletonLoader={option.useSkeletonLoader}
            disableImageCache={option.disableImageCache}
          />
        </RadioGroup.Item>
      ))}
    </RadioGroup>
  );
}

const styles = StyleSheet.create({
  radioGroup: {
    flexDirection: 'row',
    gap: 16,
  },
});

type PickerAspectCardProps = {
  title: string;
  subtitle?: string;
  image?: ImageAssetWithAltText;
  bufferImage?: boolean;
  useSkeletonLoader?: boolean;
  disableImageCache?: boolean;
};

function PickerAspectCard({
  title,
  subtitle,
  image,
  bufferImage,
  useSkeletonLoader,
  disableImageCache,
}: PickerAspectCardProps) {
  const radioItem = useRadioItem();
  const cmsImageAlt = image?.altText?.trim();

  const imageUrl = image?.asset?.uri && new URL(image.asset.uri);
  if (imageUrl && radioItem.disabled) {
    imageUrl.searchParams.set('sat', '-100');
  }

  return (
    <RadioGroup.Pressable
      pressedStyle={cardStyles.selectedContainer}
      style={[
        cardStyles.container,
        !radioItem.disabled && radioItem.selected && cardStyles.selectedContainer,
      ]}
      borderRadius={8}
      testID="product-list-item-button"
    >
      {bufferImage && useSkeletonLoader ? (
        <Skeleton
          style={[
            cardStyles.image,
            cardStyles.bufferedImage,
            !radioItem.selected && cardStyles.unselectedImage,
            radioItem.disabled && cardStyles.unavailableImage,
          ]}
        />
      ) : imageUrl ? (
        <Image
          style={[
            cardStyles.image,
            !radioItem.selected && cardStyles.unselectedImage,
            radioItem.disabled && cardStyles.unavailableImage,
          ]}
          cachePolicy={disableImageCache ? 'none' : 'memory-disk'}
          contentFit="contain"
          accessibilityLabel={cmsImageAlt || title}
          alt={cmsImageAlt || title}
          source={imageUrl.toString()}
          placeholder={image?.asset?.blurHash ? { blurhash: image.asset.blurHash } : undefined}
        />
      ) : null}
      <PickerAspectCardTextContainer
        selected={radioItem.selected}
        title={title}
        subtitle={radioItem.disabled ? 'Unavailable' : subtitle}
        disabled={radioItem.disabled}
        useSkeletonLoader={useSkeletonLoader}
        bufferImage={bufferImage}
      />
    </RadioGroup.Pressable>
  );
}

function PickerAspectCardTextContainer({
  selected,
  title,
  subtitle,
  disabled,
  useSkeletonLoader,
  bufferImage,
}: {
  selected: boolean;
  title: string;
  subtitle?: string;
  disabled?: boolean;
  useSkeletonLoader?: boolean;
  bufferImage?: boolean;
}) {
  const pressableState = usePressableState();
  const shouldTextBeMuted =
    (!pressableState.pressed && !selected) || pressableState.disabled || disabled;

  return (
    <YStack style={cardStyles.textContainer}>
      {bufferImage && useSkeletonLoader ? (
        <>
          <Skeleton
            style={[cardStyles.titleSkeleton, shouldTextBeMuted && cardStyles.textMutedSkeleton]}
          />
          <Skeleton
            style={[cardStyles.subtitleSkeleton, shouldTextBeMuted && cardStyles.textMutedSkeleton]}
          />
        </>
      ) : (
        <Text.Ui
          size="md"
          weight="semibold"
          style={[cardStyles.text, shouldTextBeMuted && cardStyles.textMuted]}
        >
          {title}
        </Text.Ui>
      )}
      {!bufferImage || !useSkeletonLoader ? (
        subtitle ? (
        <Text.Ui
          size="sm"
          numberOfLines={1}
          style={[cardStyles.text, shouldTextBeMuted && cardStyles.textMuted]}
        >
          {subtitle}
        </Text.Ui>
        ) : null
      ) : null}
    </YStack>
  );
}

const cardStyles = StyleSheet.create({
  container: {
    flexBasis: 100,
    flexShrink: 0,
    flexGrow: 1,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: tokens.colors.$blackOpacity10,
    paddingTop: 20,
    paddingRight: 12,
    paddingBottom: 16,
    paddingLeft: 12,
    gap: 8,
  },
  selectedContainer: {
    borderColor: tokens.colors.$houseYellow,
  },
  image: {
    height: 40,
    width: '100%',
  },
  bufferedImage: {
    alignSelf: 'center',
    width: '72%',
    overflow: 'hidden',
    backgroundColor: tokens.colors.$houseLight,
    borderRadius: 4,
  },
  unselectedImage: {
    opacity: 0.5,
  },
  unavailableImage: {
    opacity: 0.3,
  },
  textContainer: {
    alignItems: 'center',
    maxWidth: '100%',
    gap: 2,
  },
  text: {
    textAlign: 'center',
  },
  titleSkeleton: {
    height: 16,
    width: 72,
    borderRadius: 10,
    backgroundColor: tokens.colors.$houseLight,
  },
  textMuted: {
    color: tokens.colors.$disabledText,
  },
  subtitleSkeleton: {
    height: 14,
    width: 44,
    borderRadius: 10,
    backgroundColor: tokens.colors.$houseLight,
  },
  textMutedSkeleton: {
    opacity: 0.6,
  },
});
